import { useState, useRef, useCallback, useEffect } from 'react'
import { toast } from 'react-toastify'
import { getBpPlanningConstraints, syncBpPlanningConstraints } from 'services/BpService'

export const TERMINAL_STATUSES = ['synced', 'not_found', 'failed']
const IN_PROGRESS = ['pending', 'syncing', 'rate_limited']
const POLL_MS = 4000
const STOP_AFTER_MS = 120000

/**
 * Loads the planning constraints dashboard for a building and keeps polling
 * while a screening is running (mirrors useEpcSync for units).
 */
export const usePlanningConstraints = ({ bpId }) => {
  const [dashboard, setDashboard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState(null)
  const pollRef = useRef(null)
  const stopRef = useRef(null)

  const clearTimers = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current)
    if (stopRef.current) clearTimeout(stopRef.current)
    pollRef.current = null
    stopRef.current = null
  }, [])

  const startPolling = useCallback(() => {
    clearTimers()
    setChecking(true)
    pollRef.current = setInterval(async () => {
      try {
        const res = await getBpPlanningConstraints({ bpId })
        const next = res?.data?.data
        if (next) setDashboard(next)
        if (TERMINAL_STATUSES.includes(next?.status)) {
          clearTimers()
          setChecking(false)
        }
      } catch {
        // transient — keep polling until the stop timer fires
      }
    }, POLL_MS)
    stopRef.current = setTimeout(() => {
      clearTimers()
      setChecking(false)
    }, STOP_AFTER_MS)
  }, [bpId, clearTimers])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setDashboard(null)
    clearTimers()

    if (!bpId) {
      setLoading(false)
      return undefined
    }

    getBpPlanningConstraints({ bpId })
      .then(res => {
        if (cancelled) return
        const data = res?.data?.data || null
        setDashboard(data)
        if (IN_PROGRESS.includes(data?.status)) startPolling()
      })
      .catch(e => {
        if (cancelled) return
        setError(e?.response?.data?.message || 'Could not load planning constraints.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
      clearTimers()
    }
  }, [bpId, clearTimers, startPolling])

  const check = useCallback(async () => {
    if (checking || !bpId) return
    setChecking(true)
    setError(null)
    try {
      const res = await syncBpPlanningConstraints({ bpId })
      const data = res?.data?.data
      if (data) setDashboard(data)
      // A sync queue driver (local dev) finishes inside the request.
      if (TERMINAL_STATUSES.includes(data?.status)) {
        setChecking(false)
      } else {
        startPolling()
      }
    } catch (e) {
      setChecking(false)
      toast.error(e?.response?.data?.message || 'Could not start the planning data check. Please try again.')
    }
  }, [bpId, checking, startPolling])

  return { dashboard, loading, checking, error, check }
}
